/**
 * Topography (topo round — docs/MODELER.md "TOPOGRAPHY element"). MESH (TIN), not
 * NURBS: survey data IS points, so INTENT = the `points` data param (spot points with
 * project-relative elevations; the site datum makes them real-world) plus optional
 * `breaklines`. The Delaunay TIN, 1'/5' contours, section earth poché, and the
 * elevation grade line all DERIVE. GRADING = the `modifiers` stack (pads with
 * daylight slopes, retaining edges, surface-material regions) — non-destructive,
 * explicit stack-order precedence, never a second surface (see types.ts#TopoModifier).
 *
 * No `pathParamKey`: the topo has no drawn boundary — its hull derives from the
 * points, so whole-path translation / corner joins / generic path handles never
 * apply. Contour intervals and the daylight slope are TYPE params (firm standards,
 * per the types doctrine); the surface material paints the un-regioned TIN and
 * supplies the section cut poché ('' = the seeded Earth material by name).
 */
import type { CategorySchema } from '../../registry'

export const topoSchema: CategorySchema = {
  id: 'topo',
  label: 'Topography',
  // buildings pick first — the ground is always the fallback candidate (3D pick
  // ordering; the plan-side twin is pickConfigs' planPriority)
  pickRank: 20,
  typeParams: {
    groups: [
      {
        label: 'Contours',
        params: [
          { key: 'minorIntervalFt', label: 'Minor Interval', kind: 'length', default: 1, min: 0.25, max: 10 },
          { key: 'majorIntervalFt', label: 'Major Interval', kind: 'length', default: 5, min: 1, max: 50 },
        ],
      },
      {
        label: 'Grading',
        params: [
          { key: 'defaultSlopeH', label: 'Daylight Slope (H per 1 V)', kind: 'number', default: 2, min: 0.25, max: 10 },
          { key: 'baseDepthFt', label: 'Mass Depth Below Low Point', kind: 'length', default: 5, min: 1, max: 50 },
        ],
      },
      {
        label: 'Surface',
        params: [
          // the un-regioned TIN's material (3D drape + section cut poché); '' falls
          // back to the seeded Earth material by name. A TYPE param on purpose —
          // an instance-schema twin would shadow it in resolution (the roof
          // overhangFt lesson); per-area looks are the REGION modifiers' job.
          { key: 'surfaceMaterial', label: 'Surface Material', kind: 'material', default: '' },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Terrain Data',
        params: [
          { key: 'points', label: 'Spot Points', kind: 'data', default: null },
          { key: 'breaklines', label: 'Breaklines', kind: 'data', default: null },
          { key: 'modifiers', label: 'Modifier Stack', kind: 'data', default: null },
        ],
      },
    ],
  },
}
